/**
 * useVoiceConversation — shared voice mode state + handlers for any chat surface.
 *
 * Owns:
 *   - voiceMode toggle (with optional default)
 *   - record / stop / transcribe pipeline (push-to-talk)
 *   - auto-play of assistant responses, with two modes:
 *       * speak(fullText) — single TTS request, fires once message is complete
 *       * beginUtterance() / feedTextDelta(chunk) / endUtterance() — sentence-chunk
 *         TTS: synthesize and play each sentence as it streams in, drastically
 *         reducing time-to-first-audio
 *   - error state + interruption (stopSpeaking)
 *
 * Caller wires up:
 *   const voice = useVoiceConversation({
 *     enabled: subscription?.tier === 'pro' || subscription?.tier === 'entrepreneur',
 *     defaultOn: mode === 'review',
 *     onTranscribed: (text) => handleSendWithText(text),
 *   });
 *
 * For streaming chat, use begin/feed/end around the SSE loop.
 * For non-streaming, call speak(text) when the response is ready.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { transcribeAudio, speakStream } from './api';
import { useMediaRecorder } from './useMediaRecorder';
import { playStreamingMp3, createChainedPlayer, isMseSupported, playTurnTone } from './voicePlayer';
import { stripMarkdown } from './markdownForSpeech';

// Abbreviations we should NOT treat as sentence boundaries.
const ABBREV = new Set([
  'mr', 'mrs', 'ms', 'dr', 'jr', 'sr', 'st', 'vs',
  'etc', 'prof', 'sen', 'rep', 'vol', 'no',
  'e.g', 'i.e', 'u.s', 'a.m', 'p.m',
]);

function isAbbreviation(buffer, periodIdx) {
  let start = periodIdx;
  while (start > 0 && /[A-Za-z.]/.test(buffer[start - 1])) start--;
  const tok = buffer.slice(start, periodIdx).toLowerCase();
  if (!tok) return false;
  if (ABBREV.has(tok)) return true;
  if (/^[a-z]$/i.test(tok)) return true; // single-letter initial
  return false;
}

function isDecimal(buffer, periodIdx) {
  return periodIdx > 0
    && periodIdx + 1 < buffer.length
    && /\d/.test(buffer[periodIdx - 1])
    && /\d/.test(buffer[periodIdx + 1]);
}

/**
 * Extract complete sentences from a streaming buffer.
 * Returns { sentences: string[], remainder: string } — remainder is the
 * partial trailing text that hasn't hit a sentence boundary yet.
 */
export function extractSentences(buffer) {
  const sentences = [];
  let start = 0;
  let i = 0;
  while (i < buffer.length) {
    const ch = buffer[i];
    if (ch === '.' || ch === '!' || ch === '?') {
      // Absorb adjacent terminators like "!!", "?!", "..."
      let endPunct = i;
      while (endPunct + 1 < buffer.length && /[.!?]/.test(buffer[endPunct + 1])) endPunct++;
      const next = buffer[endPunct + 1];
      if (next === undefined) break; // partial — wait for more
      // Treat closing markdown markers (*, _) as boundary characters so
      // bolded/italicized sentences like "**Key insight.**" split cleanly.
      const nextIsBoundary = /\s/.test(next) || /[")\]*_]/.test(next);
      if (nextIsBoundary) {
        // Abbreviation / decimal guard for a single '.'
        if (i === endPunct && ch === '.'
            && (isAbbreviation(buffer, i) || isDecimal(buffer, i))) {
          i++;
          continue;
        }
        let sentenceEnd = endPunct + 1;
        while (sentenceEnd < buffer.length && /[")\]*_]/.test(buffer[sentenceEnd])) sentenceEnd++;
        const sent = buffer.slice(start, sentenceEnd).trim();
        if (sent) sentences.push(sent);
        i = sentenceEnd;
        while (i < buffer.length && /\s/.test(buffer[i])) i++;
        start = i;
        continue;
      }
      i = endPunct + 1;
      continue;
    }
    if (ch === '\n' && buffer[i + 1] === '\n') {
      const sent = buffer.slice(start, i).trim();
      if (sent) sentences.push(sent);
      i += 2;
      while (i < buffer.length && /\s/.test(buffer[i])) i++;
      start = i;
      continue;
    }
    i++;
  }
  return { sentences, remainder: buffer.slice(start) };
}

export function useVoiceConversation({ enabled = false, defaultOn = false, onTranscribed, voiceId, onVoiceModeChange } = {}) {
  const [voiceMode, setVoiceMode] = useState(enabled && defaultOn);
  const [transcribing, setTranscribing] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [error, setError] = useState(null);
  const playerRef = useRef(null);
  const chainedRef = useRef(null);
  const utteranceRef = useRef({ buffer: '', active: false, previousText: '' });

  const { isRecording, isSupported: micSupported, start, stop, durationMs } = useMediaRecorder();

  const stopSpeaking = useCallback(() => {
    if (playerRef.current) {
      try { playerRef.current.stop(); } catch { /* noop */ }
      playerRef.current = null;
    }
    if (chainedRef.current) {
      try { chainedRef.current.stop(); } catch { /* noop */ }
      chainedRef.current = null;
    }
    utteranceRef.current = { buffer: '', active: false, previousText: '' };
    setSpeaking(false);
  }, []);

  const speak = useCallback(async (text) => {
    if (!voiceMode || !text || !text.trim()) return;
    const clean = stripMarkdown(text);
    if (!clean) return;
    if (playerRef.current) {
      try { playerRef.current.stop(); } catch { /* noop */ }
      playerRef.current = null;
    }
    setSpeaking(true);
    try {
      const response = await speakStream(clean, voiceId);
      const player = await playStreamingMp3(response, {
        onEnded: () => {
          setSpeaking(false);
          playerRef.current = null;
          playTurnTone();
        },
        onError: (e) => { setError(e.message); setSpeaking(false); },
      });
      playerRef.current = player;
    } catch (e) {
      setError(e.message);
      setSpeaking(false);
    }
  }, [voiceMode]);

  const beginUtterance = useCallback(async () => {
    if (!voiceMode) return;
    // Cancel anything in-flight
    if (playerRef.current) {
      try { playerRef.current.stop(); } catch { /* noop */ }
      playerRef.current = null;
    }
    if (chainedRef.current) {
      try { chainedRef.current.stop(); } catch { /* noop */ }
      chainedRef.current = null;
    }
    utteranceRef.current = { buffer: '', active: true, previousText: '' };

    if (!isMseSupported()) {
      // Fallback path: buffer text and issue one request on endUtterance
      return;
    }
    setSpeaking(true);
    try {
      const chained = await createChainedPlayer({
        onEnded: () => {
          setSpeaking(false);
          chainedRef.current = null;
          playTurnTone();
        },
        onError: (e) => { setError(e.message); setSpeaking(false); },
      });
      chainedRef.current = chained;
    } catch (e) {
      setError(e.message);
      setSpeaking(false);
    }
  }, [voiceMode]);

  const feedTextDelta = useCallback((chunk) => {
    if (!voiceMode || !utteranceRef.current.active || !chunk) return;
    utteranceRef.current.buffer += chunk;

    // Non-MSE fallback: just accumulate; endUtterance will fire a single speak()
    if (!chainedRef.current) return;

    const { sentences, remainder } = extractSentences(utteranceRef.current.buffer);
    utteranceRef.current.buffer = remainder;
    for (const sentence of sentences) {
      const clean = stripMarkdown(sentence);
      if (!clean) continue;
      const prev = utteranceRef.current.previousText;
      chainedRef.current.enqueue(speakStream(clean, voiceId, prev || undefined));
      utteranceRef.current.previousText = clean;
    }
  }, [voiceMode, voiceId]);

  const endUtterance = useCallback(() => {
    if (!voiceMode || !utteranceRef.current.active) return;
    const trailing = utteranceRef.current.buffer.trim();
    const prev = utteranceRef.current.previousText;
    utteranceRef.current.buffer = '';
    utteranceRef.current.active = false;

    if (chainedRef.current) {
      if (trailing) {
        const clean = stripMarkdown(trailing);
        if (clean) chainedRef.current.enqueue(speakStream(clean, voiceId, prev || undefined));
      }
      chainedRef.current.end();
      return;
    }
    // Non-MSE fallback: buffer holds all accumulated text (speak() strips internally)
    if (trailing) speak(trailing);
  }, [voiceMode, speak, voiceId]);

  const toggleVoiceMode = useCallback(() => {
    setVoiceMode((v) => {
      const next = !v;
      if (!next) stopSpeaking();
      if (onVoiceModeChange) {
        try { onVoiceModeChange(next); } catch { /* fire-and-forget */ }
      }
      return next;
    });
  }, [stopSpeaking, onVoiceModeChange]);

  const startRecording = useCallback(async () => {
    setError(null);
    if (speaking) stopSpeaking();
    try {
      await start();
    } catch (e) {
      setError(e.message);
    }
  }, [speaking, stopSpeaking, start]);

  const stopRecordingAndSend = useCallback(async () => {
    if (!isRecording) return;
    const blob = await stop();
    if (!blob || blob.size < 1000) return; // too short — likely misclick
    setTranscribing(true);
    try {
      const result = await transcribeAudio(blob);
      const text = (result.text || '').trim();
      if (text && onTranscribed) {
        await onTranscribed(text);
      }
    } catch (e) {
      setError(`Transcribe: ${e.message}`);
    } finally {
      setTranscribing(false);
    }
  }, [isRecording, stop, onTranscribed]);

  useEffect(() => {
    return () => {
      if (playerRef.current) { try { playerRef.current.stop(); } catch { /* noop */ } }
      if (chainedRef.current) { try { chainedRef.current.stop(); } catch { /* noop */ } }
    };
  }, []);

  return {
    available: enabled && micSupported,
    voiceMode,
    toggleVoiceMode,
    isRecording,
    transcribing,
    speaking,
    durationMs,
    error,
    clearError: () => setError(null),
    startRecording,
    stopRecordingAndSend,
    speak,
    beginUtterance,
    feedTextDelta,
    endUtterance,
    stopSpeaking,
  };
}
